import { Component, effect, EventEmitter, inject, Output } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { fetchUserAttributes, fetchAuthSession } from '@aws-amplify/auth';
import { UserService } from 'src/app/services/user';
import { LocaleService } from 'src/app/services/locale';
import { CompanyComponent } from '../../shared/company';
import { PaymentService } from 'src/app/services/payment';
import { CompanyService } from 'src/app/services/company';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-sidebar',
  imports: [RouterModule, CommonModule, CompanyComponent],
  template: `
    <div class="flex flex-col px-3 sm:px-5 xl:px-6">
      <div class="futura text-xs sm:text-sm text-[#043B22]">{{ locale.dashboard.sidebar_favorites }}</div>
      <div *ngIf="favorites.length < 1" class="mt-2.5 sm:mt-3 text-xs sm:text-sm text-[#616161] px-1.5">
        {{ locale.dashboard.sidebar_no_favorites }}
      </div>
      <div class="flex flex-col py-2.5 sm:py-3 gap-1.5 overflow-y-auto !max-h-[300px]" style="scrollbar-width: thin;">
        <div *ngFor="let company of favorites; let x = index" class="rounded-lg hover:bg-gray-100 transition-all duration-200 cursor-pointer">
          <app-company
            [company]="company"
            [refresh]="refreshCompanies"
            [selectCompany]="onSelectCompany"
            [currentCompany]="currentCompany"
          ></app-company>
        </div>
      </div>
      <div class="futura text-xs sm:text-sm mt-3 text-[#043B22]">
        {{ locale.dashboard.sidebar_companies }}
      </div>
      <div *ngIf="companies.length < 1" class="text-xs sm:text-sm text-[#616161] mt-2.5 sm:mt-3 px-1.5">
        {{ locale.dashboard.sidebar_no_companies }}
      </div>
      <div class="flex flex-col py-2.5 sm:py-3 gap-1.5 overflow-y-auto !max-h-[300px]" style="scrollbar-width: thin;">
        <div *ngFor="let company of companies; let x = index" class="rounded-lg hover:bg-gray-100 transition-all duration-200 cursor-pointer">
          <app-company
            [company]="company"
            [refresh]="refreshCompanies"
            [selectCompany]="onSelectCompany"
            [currentCompany]="currentCompany"
          ></app-company>
        </div>
      </div>
      <!-- Fixed bottom buttons -->
      <div class="fixed bottom-3 mb-5 sm:bottom-6 left-3 sm:left-5 xl:left-6 w-[calc(280px-1.5rem)] sm:w-[calc(320px-2.5rem)] xl:w-[calc(380px-3rem)] flex flex-col gap-2.5">
        <a *ngIf="(isInvitedUser ? companies?.length < 1 : companies?.length < licenses)" routerLink="/dashboard/company" (click)="resetCompany()"
          class="backdrop-blur-3xl w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border-2 border-[#043B22] futura text-xs sm:text-sm flex gap-2 items-center justify-center rounded-xl hover:bg-[#043B22] hover:text-white cursor-pointer transition-all duration-200 bg-white/95 text-[#043B22] font-medium shadow-sm hover:shadow-md">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          {{ locale.dashboard.button_new_company }}
        </a>

        <a *ngIf="licenses === 0 || isCancelledSubscription" routerLink="/dashboard/profile"
          class="backdrop-blur-3xl w-full px-2.5 sm:px-3 py-2 sm:py-2.5 futura text-xs sm:text-sm flex gap-2 items-center justify-center rounded-xl cursor-pointer transition-all duration-200 font-medium shadow-sm hover:shadow-md bg-[#043B22] text-white hover:bg-[#065a35]">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
          </svg>
          {{ locale.common.subscribe }}
        </a>
      </div>
    </div>
  `
})
export class SidebarComponent {
  @Output() companySelected = new EventEmitter<void>();

  private userService = inject(UserService);
  private companyService = inject(CompanyService);
  private router: Router = inject(Router);
  private paymentService = inject(PaymentService);

  localeService = inject(LocaleService);
  locale = this.localeService.locale();

  licenses: number = 0;
  isCancelledSubscription: boolean = false;
  isInvitedUser = localStorage.getItem('isInvitedUser') === 'true';
  currentUser = this.userService.user();

  favorites: any = [];
  companies = this.companyService.companies();
  currentCompany = this.companyService.company();

  // Flag to prevent multiple subscription fetches
  private subscriptionFetched = false;

  constructor() {
    effect(() => {
      this.locale = this.localeService.locale();
      this.currentUser = this.userService.user();
      this.companies = this.companyService.companies();
      this.currentCompany = this.companyService.company();

      // Use effective plan details (includes cancelled but not expired subscriptions)
      const planDetails = this.paymentService.getEffectivePlanDetails();
      this.licenses = planDetails?.licenses ?? 0;

      // Invited users with active subscription get full access
      this.isInvitedUser = localStorage.getItem('isInvitedUser') === 'true' && this.licenses === 0;

      // If no plan from database, fetch Stripe subscription (only once)
      if (!this.paymentService.getCurrentPlanDetails() && this.currentUser && !this.subscriptionFetched) {
        this.subscriptionFetched = true;
        this.fetchStripeSubscription();
      }

      this.setFavorites();
      this.setCompany();
    });
  }

  fetchStripeSubscription = async () => {
    try {
      const userEmail = (await fetchUserAttributes()).email;
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();

      if (authToken && userEmail) {
        const response = await fetch(environment.lambdaUrls.stripe, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            email: userEmail,
            action: 'get_subscription'
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.subscription) {
            this.paymentService.stripeSubscription.set(data.subscription);
            // Check if subscription is cancelled but not expired
            this.isCancelledSubscription = data.subscription.cancel_at_period_end && data.subscription.status === 'active';
            // Update licenses from Stripe subscription
            const planDetails = this.paymentService.getPlanDetailsFromStripeSubscription();
            if (planDetails?.licenses) {
              this.licenses = planDetails.licenses;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching Stripe subscription:', error);
    }
  };

  sort = (a: any, b: any) => {
    if (a.companyName < b.companyName) {
      return -1;
    }
    if (a.companyName > b.companyName) {
      return 1;
    }
    return 0;
  };

  refreshCompanies = () => {
    this.companyService.getCompanies();
  };

  setFavorites = async () => {
    try {
      if (this.companies && this.companies?.length > 0) {
        this.favorites = this.companies.sort(this.sort).filter((x: any) => x?.isFavorite);
        this.companies = this.companies.sort(this.sort);
      }
    } catch (error) { }
  };

  setCompany = () => {
    const companyId = localStorage.getItem('companyId');
    if (!companyId) return;
    const company = this.companies.find((x: any) => x.id === companyId);
    if (company) {
      this.selectCompany(company, true);
    }
  };

  resetCompany = () => {
    localStorage.removeItem('companyId');
    localStorage.removeItem('companyDetails');
    this.companyService.company.set(null);
    this.router.navigateByUrl('/dashboard/company');
  };

  selectCompany = async (company: any, noRoute?: boolean) => {
    if (company) {
      localStorage.setItem('companyId', company.id);
      localStorage.setItem('companyDetails', JSON.stringify(company));
      this.companyService.company.set(company);
      if (!noRoute) this.router.navigateByUrl('/dashboard/calculations');
    }
  };

  onSelectCompany = async (company: any, noRoute?: boolean) => {
    await this.selectCompany(company, noRoute);
    this.companySelected.emit();
  };
}
